import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild, booleanAttribute, inject } from '@angular/core';
import { LoadingIndicatorComponent } from './loading-indicator.component';

/** Presentation only: callers own the operation, error handling, and retry policy. */
@Component({
  selector: 'app-loading-container',
  standalone: true,
  imports: [LoadingIndicatorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.is-loading]': 'loading' },
  template: `
    <div #content class="content" [class.dimmed]="loading && mode === 'overlay'"
      [style.visibility]="loading && mode === 'replace' ? 'hidden' : null"
      [attr.inert]="loading ? '' : null" [attr.aria-busy]="loading">
      <ng-content />
    </div>
    <div #status class="loading-layer" [hidden]="!loading" tabindex="-1" [attr.aria-label]="message">
      @if (loading) {
        <app-loading-indicator [message]="message" />
      }
    </div>
  `,
  styles: [`
    :host { display: block; position: relative; border-radius: inherit; }
    :host(.is-loading) { min-height: 7rem; }
    .dimmed { opacity: .75; }
    .loading-layer { position: absolute; inset: 0; z-index: 10; display: flex; align-items: flex-start; justify-content: center; padding: 1rem; border-radius: inherit; background: rgb(255 255 255 / .35); cursor: progress; }
    .loading-layer[hidden] { display: none; }
    .loading-layer:focus { outline: none; }
    app-loading-indicator { position: sticky; top: max(1rem, calc(50vh - 2rem)); max-width: 100%; border: 1px solid #e2e8f0; border-radius: 1rem; background: white; box-shadow: 0 4px 16px rgb(15 23 42 / .06); }
  `]
})
export class LoadingContainerComponent implements OnChanges, OnDestroy {
  @Input({ transform: booleanAttribute }) loading = false;
  @Input() mode: 'replace' | 'overlay' = 'overlay';
  @Input() message = 'Loading…';

  @ViewChild('content', { static: true }) private content?: ElementRef<HTMLElement>;
  @ViewChild('status', { static: true }) private status?: ElementRef<HTMLElement>;
  private readonly document = inject(DOCUMENT);
  private previousFocus: HTMLElement | null = null;
  private destroyed = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['loading']) return;
    const active = this.document.activeElement as HTMLElement | null;
    if (this.loading && active && this.content?.nativeElement.contains(active)) {
      this.previousFocus = active;
      queueMicrotask(() => {
        if (!this.destroyed && this.loading) this.status?.nativeElement.focus({ preventScroll: true });
      });
    } else if (!this.loading && this.previousFocus) {
      const previous = this.previousFocus;
      this.previousFocus = null;
      queueMicrotask(() => {
        if (!this.destroyed && !this.loading && previous.isConnected && !previous.closest('[inert]') &&
            (this.document.activeElement === this.status?.nativeElement || this.document.activeElement === this.document.body)) {
          previous.focus({ preventScroll: true });
        }
      });
    }
  }

  ngOnDestroy(): void { this.destroyed = true; }
}
